import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useCallback, useRef, useState } from "react";

import { sanitizeFileName } from "../../../handlers/documentStorage";
import { docSubTypes, dspDocTypes } from "../../../handlers/enums";
import { getErrorMessage, isConflictError } from "../../../handlers/errorHandling";
import { generateFileHash } from "../../../handlers/utilityFunctions";
import { API } from "../../../services/apiService";
import { findDspIdByFileHash, primeUserAccount } from "../../../services/create-resume/queries";
import FileUploadService from "../../../services/fileUploadService";
import {
	clearResumeSetup,
	getPendingSignup,
	type PendingSignup,
	type ResumeSetupState,
	setResumeSetup,
} from "../pendingSignup";

/** How long we watch an extraction before handing the user their profile anyway. */
export const EXTRACTION_TIMEOUT_MS = 90 * 1000;

/**
 * What to do with a resume setup found in the draft on the way back into /onboarding.
 *
 * - `restart`    — nothing happened yet. Offer the upload from the top.
 * - `needs-file` — the account exists but the resume does not. Ask for the file again.
 * - `dead-end`   — the row was written and the file never landed. That file is spent.
 * - `poll`       — the file is in S3; watch the extraction.
 * - `finish`     — the file is in S3 but there is no id to watch. Send them to their profile.
 */
export type ResumeSetupRecovery =
	| { action: "restart" }
	| { action: "needs-file" }
	| { action: "dead-end" }
	| { action: "poll"; dspId: string; startedAt: number }
	| { action: "finish" };

/**
 * Reconcile the stored phase against what the backend actually has.
 *
 * The case worth spelling out is `account` with a record already present: the POST landed but the
 * page died before its response was read. The phase says "in flight", the server says "done", and
 * the server wins.
 */
export function reconcileResumeSetup(
	resumeSetup: ResumeSetupState | undefined,
	hasUserRecord: boolean,
): ResumeSetupRecovery {
	if (!resumeSetup) return { action: "restart" };

	switch (resumeSetup.phase) {
		case "account":
			return hasUserRecord ? { action: "needs-file" } : { action: "restart" };

		case "dsp":
			return { action: "needs-file" };

		case "upload":
			// The row claims the file hash and cannot be deleted — the delete endpoint refuses Resume
			// documents. Sending the same file again would 409 every time.
			return { action: "dead-end" };

		case "extract":
			return resumeSetup.dspId
				? { action: "poll", dspId: resumeSetup.dspId, startedAt: resumeSetup.startedAt ?? Date.now() }
				: { action: "finish" };

		default:
			return { action: "restart" };
	}
}

/**
 * Whether the wait is over, measured from a persisted start time.
 *
 * Persisted rather than timed from mount, so that reloading during extraction cannot keep handing
 * the user a fresh "under a minute".
 */
export function hasExtractionTimedOut(startedAt: number, now: number = Date.now()): boolean {
	return now - startedAt >= EXTRACTION_TIMEOUT_MS;
}

/** Which request failed. Reported to analytics and used to pick the recovery offered. */
export type ResumeSetupStage = "user" | "dsp" | "s3";

export interface ResumeSetupFailure {
	stage: ResumeSetupStage;
	message: string;
	/**
	 * Whether the very same file can be sent again.
	 *
	 * False once the DSP row exists but the file did not reach S3 and the presigned URL has been
	 * lost. The API rejects a duplicate file_hash, and the row cannot be cleared: the delete
	 * endpoint refuses Resume documents (see removeDocumentByFileHash in
	 * Views/Profile/components/DocumentCenter.tsx). Offering "try again" there would fail forever.
	 */
	canRetrySameFile: boolean;
}

interface UploadInfo {
	uploadUrl?: string;
	content_type?: string;
}

/**
 * `POST smart-profiles` returns the saved dsp_info document. Its `_id` is what the smart profile
 * stores as `dsp_id`, and it is what `GET smart-profiles?dspId=` expects — the response carries no
 * field literally named `dsp_id`.
 */
type CreateDspResponse = UploadInfo & { upload_info?: UploadInfo; _id?: string; dsp_id?: string };

/** The create endpoint nests the presign; the update endpoint returns it bare. Accept either. */
function readUploadInfo(response: CreateDspResponse | undefined): UploadInfo {
	return response?.upload_info ?? response ?? {};
}

/** Only the keys the backend whitelists — it rejects unknown fields. */
function minimalAccountPayload(draft: PendingSignup) {
	return {
		emailAddress: draft.email,
		firstName: draft.firstName,
		lastName: draft.lastName,
		...(draft.phone ? { phoneNumber: draft.phone } : {}),
		// Every field on JobPreference is @IsOptional, so the resume path can ask nothing and still
		// pass validation. Sent as an empty object rather than omitted, because `jobPreferences`
		// itself carries no @IsOptional on UserDto. Nothing is invented: an empty desiredTitle or a
		// guessed area of expertise would go straight into the job matching.
		jobPreferences: {},
	};
}

function dspPayload(file: File, fileHash: string) {
	// The file name is the description everywhere a document is uploaded. doc_description is
	// capped at 140 characters server-side, so cut it here rather than take a 400.
	const description = (file.name ?? "").slice(0, 140);

	return {
		dsp_title: "My Resume",
		// The API stores the description from doc_description; dsp_description alone is accepted
		// but silently discarded. Send both.
		doc_description: description,
		dsp_description: description,
		dsp_doc_type: dspDocTypes.RESUME,
		doc_sub_type: docSubTypes.RESUME.name,
		// Normalised so the stored storage_key matches the uploaded object.
		file_name: sanitizeFileName(file.name),
		file_hash: fileHash,
	};
}

interface UseResumeSetupOptions {
	/** Called once the file is in S3 and the wait can begin. */
	onExtracting: (resumeSetup: ResumeSetupState) => void;
}

/**
 * Runs the resume path: POST /users -> POST smart-profiles -> presigned PUT.
 *
 * Every phase is written to the signup draft *before* the request it describes, so a page closed
 * mid-flight leaves a marker for the step that may already have happened. See
 * `reconcileResumeSetup` for what each marker means on the way back in.
 */
export function useResumeSetup({ onExtracting }: UseResumeSetupOptions) {
	const queryClient = useQueryClient();
	const [running, setRunning] = useState(false);
	const [failure, setFailure] = useState<ResumeSetupFailure | null>(null);

	// `running` only disables the button on the next render, so a fast second click could fire a
	// second POST /users before that lands. Same guard the questionnaire uses.
	const inFlightRef = useRef(false);

	// Held for a retry that skips straight back to the PUT. Deliberately not persisted: a File
	// cannot survive a reload, and a stale presigned URL would fail in a way we could not explain.
	const pendingUploadRef = useRef<{ file: File; uploadUrl: string; contentType?: string } | null>(null);

	const fail = useCallback((stage: ResumeSetupStage, error: unknown, canRetrySameFile: boolean, fallback: string) => {
		posthog.capture("registration_resume_upload_failed", { stage, recoverable: canRetrySameFile });
		setFailure({ stage, message: getErrorMessage(error, fallback), canRetrySameFile });
		setRunning(false);
		inFlightRef.current = false;
	}, []);

	/** Phase `extract`: the file is in S3 and the DSP is being read. */
	const enterExtraction = useCallback(
		(dspId: string | undefined, fileHash: string) => {
			const resumeSetup: ResumeSetupState = {
				phase: "extract",
				dspId,
				fileHash,
				startedAt: Date.now(),
			};
			setResumeSetup(resumeSetup);
			pendingUploadRef.current = null;
			setRunning(false);
			inFlightRef.current = false;
			onExtracting(resumeSetup);
		},
		[onExtracting],
	);

	/** The last leg, kept separate so a failed upload can be retried without a second DSP row. */
	const sendFile = useCallback(
		async (upload: { file: File; uploadUrl: string; contentType?: string }, dspId: string | undefined) => {
			const fileHash = getPendingSignup()?.resumeSetup?.fileHash ?? "";

			try {
				await new Promise<void>((resolve, reject) => {
					FileUploadService.uploadResumeFileToBucketWithFetchAPI(
						{ uploadUrl: upload.uploadUrl, file: upload.file, contentType: upload.contentType },
						() => resolve(),
						(error) => reject(error),
					);
				});
			} catch (error) {
				// The row is written before the object lands, so it is still there. Retrying the PUT
				// against the same URL is safe; creating the DSP again is not.
				fail("s3", error, true, "The file could not be uploaded. Please try again.");
				return;
			}

			enterExtraction(dspId, fileHash);
		},
		[enterExtraction, fail],
	);

	/**
	 * Create the DSP row and send the file. Split out so a reload that lands on phase `dsp` — the
	 * record exists, the row does not — can pick up here with a freshly chosen file.
	 */
	const createDspAndUpload = useCallback(
		async (file: File) => {
			let fileHash: string;

			try {
				fileHash = await generateFileHash(file);
			} catch (error) {
				fail("dsp", error, true, "Could not read that file. Please try again.");
				return;
			}

			setResumeSetup({ phase: "dsp", fileHash });

			let response: CreateDspResponse;

			try {
				const created = await API.post("smart-profiles", dspPayload(file, fileHash));
				response = created.data as CreateDspResponse;
			} catch (error) {
				// 409 means this exact file already has a row — almost always from an earlier upload
				// that failed after the row was written. That row cannot be removed for a Resume, so
				// this file is spent.
				fail(
					"dsp",
					error,
					!isConflictError(error),
					isConflictError(error)
						? "We already have a copy of this file that did not finish uploading. Please choose a different file."
						: "Could not save your resume. Please try again.",
				);
				return;
			}

			const { uploadUrl, content_type: contentType } = readUploadInfo(response);

			if (!uploadUrl) {
				fail("dsp", null, true, "The server did not return an upload link. Please try again.");
				return;
			}

			// Fall back to matching on the hash — unique, where the title is not — in case the shape
			// of the create response ever changes.
			let dspId = response?._id ?? response?.dsp_id;
			if (!dspId) {
				dspId = (await findDspIdByFileHash(fileHash).catch(() => null)) ?? undefined;
			}

			setResumeSetup({ phase: "upload", dspId, fileHash });
			pendingUploadRef.current = { file, uploadUrl, contentType };

			await sendFile({ file, uploadUrl, contentType }, dspId);
		},
		[fail, sendFile],
	);

	/** Entry point from the upload screen. Creates the account, then the DSP, then uploads. */
	const start = useCallback(
		async (file: File, draft: PendingSignup) => {
			if (inFlightRef.current) return;
			inFlightRef.current = true;
			setRunning(true);
			setFailure(null);

			// Written before the request: if the page dies before the response is read, the record
			// may exist and this marker is the only thing that says so.
			setResumeSetup({ phase: "account" });

			try {
				const { data } = await API.post("users", minimalAccountPayload(draft));
				localStorage.setItem("jobPreferences", JSON.stringify(data));
				// Same reason as the questionnaire: publish the record the server returned rather than
				// leaving /profile to re-read it and race the write.
				primeUserAccount(data);
			} catch (error) {
				// Nothing downstream ran, so the whole thing can simply be tried again — or abandoned
				// for the questionnaire, which is still untouched.
				fail("user", error, true, "We couldn't finish setting up your profile. Please try again.");
				return;
			}

			// Deliberately not invalidating ["user", "account"] here. A refetch would tell the page a
			// record exists, and the redirect it guards would pull the user out of this upload.
			await createDspAndUpload(file);
		},
		[createDspAndUpload, fail],
	);

	/** Retry after a failure, without repeating a step that already succeeded. */
	const retry = useCallback(
		async (file: File | null, draft: PendingSignup) => {
			if (inFlightRef.current || !failure) return;

			const held = pendingUploadRef.current;

			// The S3 leg failed but the presigned URL is still in hand: send the same file again.
			if (failure.stage === "s3" && held) {
				inFlightRef.current = true;
				setRunning(true);
				setFailure(null);
				await sendFile(held, getPendingSignup()?.resumeSetup?.dspId);
				return;
			}

			if (!file) return;

			inFlightRef.current = true;
			setRunning(true);
			setFailure(null);

			// The account already exists whenever the failure came from a later stage.
			if (failure.stage === "user") {
				await start(file, draft);
				return;
			}

			await createDspAndUpload(file);
		},
		[createDspAndUpload, failure, sendFile, start],
	);

	/** Give up on the resume path and leave the draft clean for whatever comes next. */
	const abandon = useCallback(() => {
		pendingUploadRef.current = null;
		inFlightRef.current = false;
		setRunning(false);
		setFailure(null);
		clearResumeSetup();
	}, []);

	/** The one place the flow ends. Reached by a finished extraction and by the wait timing out. */
	const finish = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["user", "account"] });
		queryClient.invalidateQueries({ queryKey: ["smartProfile"] });
	}, [queryClient]);

	return {
		abandon,
		failure,
		finish,
		/** Pick up from phase `dsp` on the way back in, with a newly chosen file. */
		resumeFromDsp: createDspAndUpload,
		retry,
		running,
		start,
	};
}
